import React, { useMemo, useState } from "react";
import {
  ColumnDef,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { PlusIcon } from "lucide-react";
import { ErrorType } from "@/types";
import {
  useGetFacilityUnavailabilityQuery,
  useDeleteFacilityUnavailabilityMutation,
  useGetFacilitiesQuery,
} from "@/services/facilityApis";
import { DataTableRowActions } from "./data-table-row-actions";
import usePagination from "@/hooks/use-pagination";
import Pagination from "@/components/ui/table/pagination-table";
import useTableData from "@/components/ui/table/data-table-logic";
import { DataTableColumnHeader } from "@/components/ui/table/data-table-column-header";
import { FacilityUnavailability } from "@/types/facility/unavailability";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import { toast } from "@/components/ui/use-toast";
import { displayValue, displayDate } from "@/lib/helper";
import FacilityUnavailabilityForm from "../modal/form";
import TableFilters from "@/components/ui/table/data-table-filter";

interface SearchCriteriaType {
  limit: number;
  offset: number;
  sort_order: string;
  sort_key: string;
  search_key?: string;
  facility_ids?: string[];
}

const initialValue = {
  limit: 10,
  offset: 0,
  sort_order: "desc",
  sort_key: "id",
};

export default function FacilityUnavailabilityTableView({ onBack }: { onBack?: () => void }) {
  const orgId =
    useSelector((state: RootState) => state.auth.userInfo?.user?.org_id) || 0;

  const facilities = (() => {
    try {
      return (
        JSON.parse(localStorage.getItem("accessLevels") as string).facilities ??
        "no_access"
      );
    } catch {
      return "no_access";
    }
  })();

  const {
    searchCriteria,
    query,
    toggleSortOrder,
    setSearchCriteria,
  } = useTableData(initialValue);

  const {
    data: unavailabilityData,
    isLoading,
    refetch,
  } = useGetFacilityUnavailabilityQuery(
    {
      query: query,
      orgId: orgId,
    },
    {
      skip: query === "",
    }
  );

  // Fetch facilities list to map facility_id to facility_name
  const { data: facilitiesData } = useGetFacilitiesQuery(
    {
      query: "limit=1000&sort_key=id&sort_order=desc",
      orgId: orgId,
    },
    { skip: !orgId }
  );

  const facilityOptions = useMemo(() => {
    return Array.isArray(facilitiesData?.data)
      ? facilitiesData.data.map((f) => ({
          label: f.name || "",
          value: f.id?.toString() || "",
        }))
      : [];
  }, [facilitiesData]);

  // Map of facility id -> facility name, used for rendering tags
  const facilityMap = useMemo(() => {
    const map: Record<number, string> = {};
    facilitiesData?.data?.forEach((f) => {
      if (f.id) map[f.id] = f.name || "";
    });
    return map;
  }, [facilitiesData]);

  const handleFilterChange = (field: string, value: string | string[]) => {
  setFilterData((prev) => ({ ...prev, [field]: value }));
};

  const filterDisplay = [
    {
      type: "multiselect",
      name: "facility_ids",
      label: "Facility",
      options: facilityOptions,
      function: (values: string[]) => handleFilterChange("facility_ids", values),
    },
  ];

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [action, setAction] = useState<"add" | "edit">("add");
  const [formData, setFormData] = useState<FacilityUnavailability | undefined>(undefined);
  const [openFilter, setOpenFilter] = useState(false);
  const [filterData, setFilterData] = useState<Record<string, any>>({});

  const unavailabilityTableData = React.useMemo(() => {
    if (!Array.isArray(unavailabilityData?.data)) return [];

    return unavailabilityData.data.map((item) => {
      // Map multiple facility_ids if present, otherwise fallback to single facility_id
      const facilityIds = item.facility_ids || (item.facility_id ? [item.facility_id] : []);

      return {
        ...item,
        facility_ids: facilityIds,
      };
    });
  }, [unavailabilityData]);

  // Renders facility ids as green tag badges with a "+N" overflow tooltip,
  // consistent with BusinessUnitTableView
  const renderBadges = (ids: number[] | null | undefined, map: Record<number, string>) => {
    if (!ids || ids.length === 0) return <span className="text-gray-400">-</span>;

    const names = ids.map((id) => map[id]).filter(Boolean);
    if (names.length === 0) return <span className="text-gray-400">-</span>;

    const visibleBadges = names.slice(0, 2);
    const remainingNames = names.slice(2);
    const remaining = remainingNames.length;

    return (
      <div className="flex flex-wrap gap-1 items-center">
        {visibleBadges.map((name, index) => (
          <span
            key={index}
            className="inline-block rounded-full bg-green-100 text-green-800 px-3 py-1 text-xs font-medium"
          >
            {name}
          </span>
        ))}
        {remaining > 0 && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="inline-block rounded-full bg-gray-100 text-gray-600 px-3 py-1 text-xs font-medium cursor-pointer">
                  +{remaining}
                </span>
              </TooltipTrigger>
              <TooltipContent side="top">
                <ul className="flex flex-col gap-1 p-1">
                  {remainingNames.map((name, idx) => (
                    <li key={idx} className="whitespace-nowrap">{name}</li>
                  ))}
                </ul>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>
    );
  };

  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = useState({});
  const [deleteFacilityUnavailability] =
    useDeleteFacilityUnavailabilityMutation();

  const handleAddUnavailability = () => {
    setIsDialogOpen(true);
    setAction("add");
    setFormData(undefined);
  };

  const handleEditUnavailability = (data: FacilityUnavailability) => {
    setIsDialogOpen(true);
    setAction("edit");
    setFormData(data);
  };

  const handleDeleteUnavailability = async (data: FacilityUnavailability) => {
    try {
      const resp = await deleteFacilityUnavailability(
        data.id as number
      ).unwrap();
      if (resp) {
        if (
          unavailabilityData?.filtered_counts === 1 &&
          searchCriteria.offset > 0
        ) {
          setSearchCriteria((prev) => ({
            ...prev,
            offset: prev.offset - 1,
          }));
        }
        refetch();
        toast({
          variant: "success",
          title: "Unavailability Record Deleted Successfully",
        });
      }
    } catch (error) {
      console.error("Error", { error });
      if (error && typeof error === "object" && "data" in error) {
        const typedError = error as ErrorType;
        toast({
          variant: "destructive",
          title: "Error in Deletion",
          description: `${
            typedError.data?.detail ||
            (typedError.data as { message?: string }).message
          }`,
        });
      } else {
        toast({
          variant: "destructive",
          title: "Error in Deletion",
          description: `Something Went Wrong.`,
        });
      }
    }
  };

  const actionsColumn: ColumnDef<FacilityUnavailability> = {
    id: "actions",
    header: "Actions",
    maxSize: 100,
    cell: ({ row }) => (
      <DataTableRowActions
        access={facilities}
        data={row.original}
        handleEdit={handleEditUnavailability}
        handleDelete={handleDeleteUnavailability}
      />
    ),
  };

  const columns: ColumnDef<FacilityUnavailability>[] = [
    {
      accessorKey: "facility_ids",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Facility Name"
          sortKey="facility_name"
          sortable={false}
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) =>
        renderBadges(row.original.facility_ids ?? [], facilityMap),
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "start_date",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Start Date"
          sortKey="start_date"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <p>
            {row.original.start_date
              ? displayDate(row.original.start_date)
              : "—"}
          </p>
        );
      },
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "end_date",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="End Date"
          sortKey="end_date"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <p>
            {row.original.end_date
              ? displayDate(row.original.end_date)
              : "—"}
          </p>
        );
      },
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "reason",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Reason"
          sortKey="reason"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <p className="normal-case cursor-pointer">
                    <span>
                      {displayValue(
                        `${row.original.reason || ""}`.length > 30
                          ? `${row.original.reason}`.substring(0, 30) + "..."
                          : `${row.original.reason}`
                      )}
                    </span>
                  </p>
                </TooltipTrigger>
                <TooltipContent
                  side="bottom"
                  align="end"
                  className="max-w-xs"
                >
                  <p className="normal-case text-sm break-words">
                    {displayValue(`${row?.original?.reason || ""}`)}
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        );
      },
      enableSorting: false,
      enableHiding: false,
    },

    ...(facilities !== "read" ? [actionsColumn] : []),
  ];

  const table = useReactTable({
    data: unavailabilityTableData as FacilityUnavailability[],
    columns,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: {
      sorting,
      columnVisibility,
      rowSelection,
    },
  });

  const totalRecords = unavailabilityData?.filtered_counts || 0;
  const {
    handleLimitChange,
    handleNextPage,
    handlePrevPage,
    handleFirstPage,
    handleLastPage,
    isLastPage,
  } = usePagination<SearchCriteriaType>({
    totalRecords,
    searchCriteria,
    setSearchCriteria,
  });

  return (
    <div className="w-full space-y-4">
      <div className="flex flex-col lg:flex-row lg:items-center justify-end gap-4 px-3">
        {/* Buttons Container */}
        <div className="flex flex-row lg:flex-row lg:justify-center lg:items-center gap-2">
          {onBack && (
            <Button
              className="bg-primary text-sm text-black flex items-center gap-1 lg:mb-0 h-8 px-2"
              onClick={onBack}
            >
              <i className="fa fa-arrow-left h-4 w-4"></i>
              Back
            </Button>
          )}
          <Button
            access={facilities !== "read"}
            className="bg-primary text-sm text-black flex items-center gap-1 lg:mb-0 h-8 px-2"
            onClick={handleAddUnavailability}
          >
            <PlusIcon className="h-4 w-4 " />
            Create New
          </Button>
          <button
            className={`border rounded-full size-3 text-gray-400 p-4 flex items-center justify-center ${
              Object.keys(filterData).length ? "border-primary" : ""
            }`}
            onClick={() => setOpenFilter(true)}
          >
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <i
                    className={`fa fa-filter ${
                      Object.keys(filterData).length ? "text-primary" : ""
                    }`}
                  ></i>
                </TooltipTrigger>
                <TooltipContent>
                  <p>click to apply filter</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </button>
        </div>
      </div>
      <div className="rounded-none border border-border">
        <ScrollArea className="w-full relative" hideScrollToTop={true}>
          <ScrollBar
            orientation="horizontal"
            className="relative z-30 cursor-grab h-2"
          />
          <Table className="w-full sm:max-h-[40vh] lg:max-h-[50vh] xl:max-h-[60vh] 2xl:max-h-[65vh]">
            <TableHeader className="bg-secondary">
              {table?.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    return (
                      <TableHead key={header.id}>
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                      </TableHead>
                    );
                  })}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center"
                  >
                    <div className="flex space-x-2 justify-center items-center bg-white ">
                      <div className="size-3 bg-black rounded-full animate-bounce [animation-delay:-0.3s]"></div>
                      <div className="size-3 bg-black rounded-full animate-bounce [animation-delay:-0.15s]"></div>
                      <div className="size-3 bg-black rounded-full animate-bounce"></div>
                    </div>
                  </TableCell>
                </TableRow>
              ) : table.getRowModel().rows.length ? (
                table.getRowModel().rows.map((row) => (
                  <TableRow
                    key={row.id}
                    data-state={row.getIsSelected() && "selected"}
                    onClick={(e) => {
                      if (
                        facilities !== "read" &&
                        e.target instanceof Element &&
                        !e.target.closest('[role="checkbox"]') &&
                        (e.target === e.currentTarget || e.target.closest("td"))
                      ) {
                        handleEditUnavailability(
                          row.original as unknown as FacilityUnavailability
                        );
                      }
                    }}
                    className={
                      facilities !== "read"
                        ? "cursor-pointer hover:bg-secondary"
                        : ""
                    }
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id}>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : unavailabilityTableData.length > 0 ? (
                <TableRow className="!p-0">
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center"
                  >
                    No unavailability record found.
                  </TableCell>
                </TableRow>
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center"
                  >
                    No records found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </ScrollArea>
      </div>

      {/* pagination */}
      {unavailabilityTableData.length > 0 && (
        <Pagination
          limit={searchCriteria.limit}
          offset={searchCriteria.offset}
          totalItems={totalRecords}
          onLimitChange={handleLimitChange}
          onNextPage={handleNextPage}
          onPrevPage={handlePrevPage}
          onFirstPage={handleFirstPage}
          onLastPage={handleLastPage}
          isLastPage={isLastPage}
        />
      )}

      <TableFilters
        isOpen={openFilter}
        setOpen={setOpenFilter}
        initialValue={initialValue}
        filterData={filterData}
        setFilter={setFilterData}
        setSearchCriteria={setSearchCriteria}
        filterDisplay={filterDisplay}
      />

      <FacilityUnavailabilityForm
        isOpen={isDialogOpen}
        setOpen={setIsDialogOpen}
        data={formData}
        action={action}
        setAction={setAction}
        setData={setFormData}
        refetch={refetch}
      />
    </div>
  );
}